import { ITaskHistory } from '@models/Task.model';

import { Icon as BlueIcon } from '@blueprintjs/core';

import { format } from 'date-fns';

import ProductPlaceholderPNG from '@assets/png/product.png';
import ProfileDefaultPNG from '@assets/png/profile-default.png';

import { nameToShortName } from '@utils/nameToShortName/nameToShortName';
import { IProduct } from '@models/Product.model';
import {
  Container,
  ContainerRelationsShips,
  DateContainer,
  DateIcon,
  DateText,
  DateTextDisabled,
  Empty,
  File,
  FileText,
  FileTextDisabled,
  Icon,
  Label,
  LabelContainer,
  LabelImage,
  Name,
  NameDisabled,
  PriorityArrow,
  ProductCell,
  ProductContainer,
  ProductGrid,
  ProductImage,
  ProductText,
  ProductTextDisabled,
  Status,
  Text,
  TextComment,
  TextContainer,
  TextContent,
  TextContentDisabled,
} from './HistoryContent.styles';

interface IHistoryContent {
  history: ITaskHistory;
}

export const HistoryContent = ({ history }: IHistoryContent) => {
  if (history.type === 'date') {
    const previousDate = new Date(history.previousData);
    const newDate = new Date(history.previousData);

    if (history.action === 'added') {
      return (
        <Container>
          <DateContainer>
            <DateIcon>
              <BlueIcon icon="calendar" color="#8F95B2" size={12} />
            </DateIcon>
            <DateText>{format(newDate, 'MMM d - hh:mma')}</DateText>
          </DateContainer>
        </Container>
      );
    }

    return (
      <Container>
        <DateContainer>
          <DateIcon>
            <BlueIcon icon="calendar" color="#8F95B2" size={12} />
          </DateIcon>
          <DateTextDisabled>
            {format(previousDate, 'MMM d - hh:mma')}
          </DateTextDisabled>
        </DateContainer>
        <PriorityArrow>
          <BlueIcon icon="arrow-right" color="#474D66" size={12} />
        </PriorityArrow>
        <DateContainer>
          <DateIcon>
            <BlueIcon icon="calendar" color="#8F95B2" size={12} />
          </DateIcon>
          <DateText>{format(newDate, 'MMM d - hh:mma')}</DateText>
        </DateContainer>
      </Container>
    );
  }

  if (history.type === 'comment') {
    return (
      <Container>
        <TextComment dangerouslySetInnerHTML={{ __html: history.newData }} />
      </Container>
    );
  }

  if (history.type === 'title') {
    return (
      <TextContainer>
        <TextContentDisabled>{history.previousData}</TextContentDisabled>
        <PriorityArrow>
          <BlueIcon icon="arrow-down" color="#474D66" size={12} />
        </PriorityArrow>
        <TextContent>{history.newData}</TextContent>
      </TextContainer>
    );
  }

  if (history.type === 'description') {
    return (
      <TextContainer>
        <TextContentDisabled>{history.previousData}</TextContentDisabled>
        <PriorityArrow>
          <BlueIcon icon="arrow-down" color="#474D66" size={12} />
        </PriorityArrow>
        <TextContent>{history.newData}</TextContent>
      </TextContainer>
    );
  }

  if (history.type === 'file') {
    if (history.action === 'removed') {
      return (
        <File>
          <FileTextDisabled>{history.previousData}</FileTextDisabled>
        </File>
      );
    }

    return (
      <File>
        <FileText>{history.newData}</FileText>
      </File>
    );
  }

  if (history.type === 'relationship') {
    const removedProducts: IProduct[] = JSON.parse(history.previousData);
    const addedProducts: IProduct[] = JSON.parse(history.newData);

    if (history.action === 'added') {
      return (
        <ContainerRelationsShips>
          {addedProducts.map(item => (
            <ProductContainer key={item.id}>
              <ProductGrid>
                <ProductCell>
                  <ProductImage
                    src={item.imageUrl || ProductPlaceholderPNG}
                    brokenSrc={ProductPlaceholderPNG}
                  />
                  {item.name ? (
                    <ProductText>{item.name}</ProductText>
                  ) : (
                    <Empty>Empty</Empty>
                  )}
                </ProductCell>
                <ProductCell>
                  {item.model ? (
                    <ProductText>{item.model}</ProductText>
                  ) : (
                    <Empty>Empty</Empty>
                  )}
                </ProductCell>
                <ProductCell>
                  {item.manufacturer?.name ? (
                    <ProductText>{item.manufacturer?.name}</ProductText>
                  ) : (
                    <Empty>Empty</Empty>
                  )}
                </ProductCell>
              </ProductGrid>
            </ProductContainer>
          ))}
        </ContainerRelationsShips>
      );
    }

    return (
      <ContainerRelationsShips>
        {removedProducts.map(item => (
          <ProductContainer key={item.id}>
            <ProductGrid>
              <ProductCell>
                <ProductImage
                  src={item.imageUrl || ProductPlaceholderPNG}
                  brokenSrc={ProductPlaceholderPNG}
                />
                {item.name ? (
                  <ProductTextDisabled>{item.name}</ProductTextDisabled>
                ) : (
                  <Empty>Empty</Empty>
                )}
              </ProductCell>
              <ProductCell>
                {item.model ? (
                  <ProductTextDisabled>{item.model}</ProductTextDisabled>
                ) : (
                  <Empty>Empty</Empty>
                )}
              </ProductCell>
              <ProductCell>
                {item.manufacturer?.name ? (
                  <ProductTextDisabled>
                    {item.manufacturer?.name}
                  </ProductTextDisabled>
                ) : (
                  <Empty>Empty</Empty>
                )}
              </ProductCell>
            </ProductGrid>
          </ProductContainer>
        ))}
      </ContainerRelationsShips>
    );
  }

  if (history.type === 'assignee') {
    const previousAssignee = JSON.parse(history.previousData);
    const newAssignee = JSON.parse(history.newData);

    if (history.action === 'added') {
      return (
        <Container>
          <LabelContainer>
            <LabelImage
              brokenSrc={ProfileDefaultPNG}
              src={newAssignee.avatar || ProfileDefaultPNG}
            />
            <Name>{nameToShortName(newAssignee.name)}</Name>
          </LabelContainer>
        </Container>
      );
    }

    if (history.action === 'removed') {
      return (
        <Container>
          <LabelContainer>
            <LabelImage
              brokenSrc={ProfileDefaultPNG}
              src={previousAssignee.avatar || ProfileDefaultPNG}
            />
            <NameDisabled>
              {nameToShortName(previousAssignee.name)}
            </NameDisabled>
          </LabelContainer>
        </Container>
      );
    }

    return (
      <Container>
        <LabelContainer>
          <LabelImage
            brokenSrc={ProfileDefaultPNG}
            src={previousAssignee.avatar || ProfileDefaultPNG}
          />
          <NameDisabled>{nameToShortName(previousAssignee.name)}</NameDisabled>
        </LabelContainer>
        <PriorityArrow>
          <BlueIcon icon="arrow-right" color="#474D66" size={12} />
        </PriorityArrow>
        <LabelContainer>
          <LabelImage
            brokenSrc={ProfileDefaultPNG}
            src={newAssignee.avatar || ProfileDefaultPNG}
          />
          <Name>{nameToShortName(newAssignee.name)}</Name>
        </LabelContainer>
      </Container>
    );
  }

  if (history.type === 'status') {
    const statusText = (status: string): string => {
      if (status === 'inprogress') return 'In Progress';
      if (status === 'done') return 'Done';

      return 'To Do';
    };

    const statusColor = (status: string): string => {
      if (status === 'inprogress') return '#1371FE';
      if (status === 'done') return '#249D0E';

      return '#696F8C';
    };

    return (
      <Container>
        <Status background={statusColor(history.previousData)}>
          {statusText(history.previousData)}
        </Status>
        <PriorityArrow>
          <BlueIcon icon="arrow-right" color="#474D66" size={12} />
        </PriorityArrow>
        <Status background={statusColor(history.newData)}>
          {statusText(history.newData)}
        </Status>
      </Container>
    );
  }

  if (history.type === 'priority') {
    const getTaskPriorityFlagColor = (priority: string): string => {
      if (priority === 'important') return '#FF9900';
      if (priority === 'non-critical') return '#52BD94';
      if (priority === 'urgent') return '#D14343';

      return '#C1C4D6';
    };

    const getText = (priority: string): string => {
      if (priority === 'important') return 'Important';
      if (priority === 'non-critical') return 'Non-Critical';
      if (priority === 'urgent') return 'Urgent!';

      return 'Uncertain';
    };

    return (
      <Container>
        <Label>
          <Icon>
            <BlueIcon
              icon="flag"
              color={getTaskPriorityFlagColor(history.previousData)}
            />
          </Icon>
          <Text>{getText(history.previousData)}</Text>
        </Label>
        <PriorityArrow>
          <BlueIcon icon="arrow-right" color="#474D66" size={12} />
        </PriorityArrow>
        <Label>
          <Icon>
            <BlueIcon
              icon="flag"
              color={getTaskPriorityFlagColor(history.newData)}
            />
          </Icon>
          <Text>{getText(history.newData)}</Text>
        </Label>
      </Container>
    );
  }

  return <div />;
};
